import { useState } from 'react'
import { Loader2, Send } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { Textarea } from '~/components/ui/textarea'
import type { ClarificationQuestion } from '~/api/types'

/**
 * 추가 확인 질문을 하나씩 답하는 스텝퍼.
 *
 * 질문 개수는 백엔드가 준 `questions` 배열 길이를 그대로 따른다(고정 개수 없음).
 * 예시답변(examples)이 있으면 한 줄짜리 풀폭 버튼으로 보여주고, 클릭하면 그 답으로
 * 확정한 뒤 곧바로 다음 질문으로 넘어간다. 마지막 질문에서 고르면 바로 제출한다.
 * 직접 입력칸은 항상 함께 두며, "다음"/Enter 흐름도 유지한다. 모든 질문에 답(또는
 * 명시적 스킵)이 끝났을 때만 제출되도록 제출 트리거는 마지막 질문에서만 동작한다.
 */
export function ClarificationStepper({
    questions,
    submitting,
    onAnswersChange,
    onComplete,
}: {
    questions: ClarificationQuestion[]
    submitting: boolean
    onAnswersChange: (combined: string) => void
    onComplete: (combined: string) => void
}) {
    const [current, setCurrent] = useState(0)
    const [answers, setAnswers] = useState<string[]>(() =>
        questions.map(() => '')
    )

    const total = questions.length
    const question = questions[current]
    const isLast = current === total - 1
    const answeredCount = answers.filter((a) => a.trim()).length

    const buildCombined = (next: string[]) =>
        questions
            .map((item, i) =>
                next[i]?.trim() ? `${item.question}\n→ ${next[i].trim()}` : ''
            )
            .filter(Boolean)
            .join('\n\n')

    const writeAnswer = (value: string) => {
        const next = answers.slice()
        next[current] = value
        setAnswers(next)
        onAnswersChange(buildCombined(next))
        return next
    }

    const setAnswer = (value: string) => {
        writeAnswer(value)
    }

    const goNext = () => setCurrent((c) => Math.min(total - 1, c + 1))

    // 선택지/직접 입력 후 진행. 마지막 질문이면 합친 답변으로 곧바로 제출하고,
    // 중간 질문이면 다음 질문으로만 이동한다(조기 제출 방지).
    const advance = (next: string[]) => {
        if (isLast) {
            onComplete(buildCombined(next))
            return
        }
        goNext()
    }

    // 선택지 클릭: 그 답으로 확정하고 곧바로 다음(또는 마지막이면 제출).
    const chooseExample = (value: string) => {
        if (submitting) return
        advance(writeAnswer(value))
    }

    return (
        <div className='space-y-2.5'>
            <div className='flex items-center justify-between gap-2'>
                <span className='text-muted-foreground text-xs'>
                    질문 {current + 1} / {total}
                    {answeredCount > 0 ? ` · ${answeredCount}개 답함` : ''}
                </span>
                <div className='flex gap-1' aria-hidden>
                    {questions.map((item, i) => (
                        <span
                            key={item.question}
                            className={`h-1.5 w-4 rounded-full ${
                                i === current
                                    ? 'bg-primary'
                                    : answers[i]?.trim()
                                      ? 'bg-primary/40'
                                      : 'bg-border'
                            }`}
                        />
                    ))}
                </div>
            </div>

            <p className='text-foreground text-sm font-medium'>
                {question.question}
            </p>

            {question.examples.length > 0 ? (
                <div className='grid gap-1.5'>
                    {question.examples.map((ex) => {
                        const selected = answers[current]?.trim() === ex
                        return (
                            <button
                                key={ex}
                                type='button'
                                aria-pressed={selected}
                                disabled={submitting}
                                onClick={() => chooseExample(ex)}
                                className={`flex w-full items-center justify-between gap-2 rounded-sm border px-3.5 py-2.5 text-left text-sm transition-colors disabled:opacity-60 ${
                                    selected
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border hover:border-primary hover:bg-accent text-foreground'
                                }`}>
                                <span>{ex}</span>
                                <Send className='size-3.5 shrink-0 opacity-70' />
                            </button>
                        )
                    })}
                </div>
            ) : null}

            <Textarea
                value={answers[current]}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={(e) => {
                    if (
                        e.key !== 'Enter' ||
                        e.shiftKey ||
                        e.nativeEvent.isComposing
                    )
                        return
                    e.preventDefault()
                    if (submitting) return
                    advance(answers)
                }}
                rows={2}
                placeholder={
                    question.examples.length > 0
                        ? '위 보기를 누르거나 직접 입력 후 Enter'
                        : '여기에 답을 적고 Enter 로 다음'
                }
                className='rounded-sm'
            />

            <div className='flex items-center justify-between gap-2'>
                <Button
                    type='button'
                    variant='outline'
                    disabled={current === 0}
                    onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                    className='h-9 rounded-sm'>
                    이전
                </Button>
                {isLast ? (
                    <Button
                        type='submit'
                        disabled={submitting || answeredCount === 0}
                        className='h-9 rounded-sm'>
                        {submitting ? (
                            <>
                                <Loader2 className='animate-spin' />
                                분석 중
                            </>
                        ) : (
                            <>
                                답변 완료, 분석하기
                                <Send />
                            </>
                        )}
                    </Button>
                ) : (
                    <Button
                        type='button'
                        onClick={goNext}
                        className='h-9 rounded-sm'>
                        다음
                    </Button>
                )}
            </div>
        </div>
    )
}
